import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

function useInView(ref: React.RefObject<HTMLElement | null>) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setInView(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
  return inView;
}

const SERVICES = [
  {
    href: '/services/ai-solutions',
    icon: 'ri-brain-line',
    tag: 'AI & Machine Learning',
    accent: 'teal',
    title: 'AI Solutions',
    desc: 'Custom ML models, conversational AI, predictive analytics, process automation, and computer vision — purpose-built for your industry workflows and data environment.',
    highlights: ['Machine Learning Models', 'NLP & Chatbots', 'Predictive Analytics', 'Process Automation (RPA)'],
    image: 'https://readdy.ai/api/search-image?query=artificial%20intelligence%20machine%20learning%20visualization%20glowing%20neural%20network%20nodes%20data%20flow%20teal%20green%20accent%20clean%20white%20background%20professional%20tech%20AI%20software%20dashboard%20interface%20minimal&width=600&height=380&seq=svc-ai-ov-001&orientation=landscape',
  },
  {
    href: '/services/web-applications',
    icon: 'ri-global-line',
    tag: 'Web Development',
    accent: 'teal',
    title: 'Web Applications',
    desc: 'Responsive, scalable, AI-enhanced web applications — from customer portals and SaaS platforms to enterprise dashboards, built with React/Next.js and cloud-native architecture.',
    highlights: ['Enterprise Portals', 'SaaS Platforms', 'AI-Integrated Dashboards', 'Progressive Web Apps'],
    image: 'https://readdy.ai/api/search-image?query=modern%20web%20application%20UI%20on%20laptop%20screen%20clean%20dashboard%20interface%20data%20visualization%20charts%20teal%20green%20accent%20minimal%20professional%20design%20white%20background%20enterprise%20portal%20software&width=600&height=380&seq=svc-web-ov-001&orientation=landscape',
  },
  {
    href: '/services/windows-applications',
    icon: 'ri-window-line',
    tag: 'Desktop Software',
    accent: 'amber',
    title: 'Windows Applications',
    desc: 'Enterprise-grade desktop software with deep OS integration, offline-first capability, hardware control, and AI-assisted features — built with 25 years of .NET expertise.',
    highlights: ['.NET / WPF / MAUI', 'Offline-First Architecture', 'Hardware Integration', 'Enterprise Security'],
    image: 'https://readdy.ai/api/search-image?query=windows%20desktop%20application%20enterprise%20software%20interface%20modern%20clean%20UI%20design%20with%20menus%20toolbars%20data%20grid%20on%20monitor%20screen%20professional%20amber%20accent%20business%20management%20system&width=600&height=380&seq=svc-win-ov-001&orientation=landscape',
  },
  {
    href: '/services/mobile-applications',
    icon: 'ri-smartphone-line',
    tag: 'Mobile Development',
    accent: 'teal',
    title: 'Mobile Applications',
    desc: 'Native iOS and Android apps, plus React Native cross-platform solutions — with on-device AI models, biometric authentication, offline sync, and push engagement.',
    highlights: ['iOS & Android Native', 'React Native / Flutter', 'On-Device AI Models', 'Biometric Authentication'],
    image: 'https://readdy.ai/api/search-image?query=mobile%20app%20screens%20iOS%20Android%20smartphone%20multiple%20devices%20modern%20clean%20UI%20UX%20design%20teal%20accent%20colors%20professional%20app%20interface%20flat%20lay%20white%20background%20minimalist&width=600&height=380&seq=svc-mob-ov-001&orientation=landscape',
  },
];

export default function ServicesPage() {
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        {/* Header */}
        <section className="relative pt-32 pb-16 bg-white overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-teal-50/80 blur-3xl -translate-y-1/4 translate-x-1/4" />
          </div>
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10 text-center">
            {/* <span className="inline-block bg-teal-100 text-teal-700 text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-5">
              Our Services
            </span> */}
            <h1 className="text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight mb-5">
              Comprehensive IT <span className="text-teal-500">Solutions</span>
            </h1>
            <p className="text-gray-500 text-xl max-w-2xl mx-auto mb-8">
              From AI-powered systems to custom web, desktop, and mobile applications — we deliver complete technology solutions backed by 25 years of expertise and ISO-certified quality.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-xl px-5 py-2.5">
                <i className="ri-award-fill text-teal-600" />
                <span className="text-sm font-semibold text-gray-900">ISO 9001:2015</span>
              </div>
              <div className="flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-xl px-5 py-2.5">
                <i className="ri-shield-check-fill text-teal-600" />
                <span className="text-sm font-semibold text-gray-900">ISO/IEC 27001:2022</span>
              </div>
              <div className="flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-xl px-5 py-2.5">
                <i className="ri-time-fill text-teal-600" />
                <span className="text-sm font-semibold text-gray-900">25+ Years Experience</span>
              </div>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section ref={ref} className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="flex flex-col gap-12">
              {SERVICES.map((svc, i) => {
                const isEven = i % 2 === 0;
                return (
                  <div
                    key={svc.href}
                    className={`grid lg:grid-cols-2 gap-12 items-center transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                    style={{ transitionDelay: `${i * 100}ms` }}
                  >
                    <div className={isEven ? 'order-1' : 'order-1 lg:order-2'}>
                      <div className="rounded-2xl overflow-hidden border border-gray-100">
                        <div className="w-full h-72">
                          <img src={svc.image} alt={svc.title} className="w-full h-full object-cover object-top" />
                        </div>
                      </div>
                    </div>
                    <div className={isEven ? 'order-2' : 'order-2 lg:order-1'}>
                      <div className="flex items-center gap-3 mb-5">
                        {/* <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${svc.accent === 'teal' ? 'bg-teal-100 text-teal-600' : 'bg-amber-100 text-amber-600'}`}>
                          <i className={`${svc.icon} text-2xl`} />
                        </div> */}
                        {/* <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${svc.accent === 'teal' ? 'bg-teal-50 text-teal-700 border border-teal-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
                          {svc.tag}
                        </span> */}
                      </div>
                      <h2 className="text-3xl lg:text-4xl font-extrabold text-gray-900 mb-4">{svc.title}</h2>
                      <p className="text-gray-500 text-base leading-relaxed mb-6">{svc.desc}</p>
                      <div className="grid grid-cols-2 gap-2.5 mb-8">
                        {svc.highlights.map((h) => (
                          <div key={h} className="flex items-center gap-2 text-sm text-gray-600">
                            <i className="ri-check-line text-teal-500 flex-shrink-0" /> {h}
                          </div>
                        ))}
                      </div>
                      <Link
                        to={svc.href}
                        className={`cursor-pointer whitespace-nowrap inline-flex items-center gap-2 font-semibold px-7 py-3.5 rounded-lg transition-colors ${svc.accent === 'teal' ? 'bg-teal-500 hover:bg-teal-600 text-white' : 'bg-amber-500 hover:bg-amber-600 text-white'}`}
                      >
                        Learn More <i className="ri-arrow-right-line" />
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Products CTA Banner */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="bg-white border border-gray-100 rounded-2xl p-10 flex flex-col lg:flex-row items-center justify-between gap-8">
              <div>
                {/* <span className="inline-block bg-amber-100 text-amber-700 text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-4">
                  Our Products
                </span> */}
                <h2 className="text-3xl font-extrabold text-gray-900 mb-3">Looking for Our Software Products?</h2>
                <p className="text-gray-500 text-base max-w-xl">
                  We also offer a suite of proprietary products — Magic Designing Software, POS Solution, RFID Solutions, Inventory Management, Ship Chandling System, and more.
                </p>
              </div>
              <Link
                to="/products"
                className="cursor-pointer whitespace-nowrap flex-shrink-0 bg-amber-500 hover:bg-amber-600 text-white font-semibold px-8 py-4 rounded-xl transition-colors inline-flex items-center gap-2"
              >
                Browse All Products <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-teal-600">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-extrabold text-white mb-4">Ready to Get Started?</h2>
            <p className="text-teal-100 text-base mb-8">ISO-certified quality, 25 years experience, and a team committed to your success.</p>
            <Link
              to="/contact"
              className="cursor-pointer whitespace-nowrap bg-white hover:bg-gray-50 text-teal-700 font-semibold px-8 py-4 rounded-xl transition-colors inline-flex items-center gap-2"
            >
              <i className="ri-calendar-check-line" /> Get Free Consultation
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
